import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import {
  SZ_SCORING_LABELS,
  SzButtonComponent,
  SzConfirmDialogService,
  SzIconComponent,
  SzSectionHeadComponent
} from '@ui';
import { RunCommandHandler } from '../../../application/handlers';
import { RunViewModel, SessionDrillViewModel } from '../../../application/models';
import { RunInput } from '../../../application/state';
import { NumpadSheetComponent } from '../../numpad-sheet/numpad-sheet.component';
import { NumpadSheetContext, NumpadSheetMode, NumpadSheetVariant } from '../../numpad-sheet/numpad-sheet.types';
import { SessionBestBlockComponent } from '../../session-best-block/session-best-block.component';
import { SessionBestFocal, SessionBestTile } from '../../session-best-block/session-best-block.types';
import { SessionRepListComponent } from '../../session-rep-list/session-rep-list.component';
import { buildRunRows } from '../../session-rep-list/run-rows.util';
import { SessionRunRow } from '../../session-rep-list/session-rep-list.types';

@Component({
  selector: 'lib-reps-only-run-entry',
  templateUrl: './reps-only-run-entry.component.html',
  styleUrls: ['./reps-only-run-entry.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    NumpadSheetComponent,
    SessionBestBlockComponent,
    SessionRepListComponent,
    SzButtonComponent,
    SzIconComponent,
    SzSectionHeadComponent
  ]
})
export class RepsOnlyRunEntryComponent {
  private readonly runHandler: RunCommandHandler = inject(RunCommandHandler);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);
  private readonly translate: TranslateService = inject(TranslateService);

  readonly sessionDrill: InputSignal<SessionDrillViewModel> = input.required<SessionDrillViewModel>();

  readonly sheetOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly editingRun: WritableSignal<RunViewModel | null> = signal<RunViewModel | null>(null);
  readonly sheetResetTrigger: WritableSignal<number> = signal<number>(0);

  readonly sheetVariant: Signal<NumpadSheetVariant> = computed(() => 'reps' as const);
  readonly sheetMode: Signal<NumpadSheetMode> = computed(() => (this.editingRun() === null ? 'add' : 'edit'));

  readonly sheetInitialPayload: Signal<RunInput | null> = computed((): RunInput | null => {
    const editing: RunViewModel | null = this.editingRun();
    if (editing === null) return null;
    return { reps: editing.reps, parTimeSeconds: editing.parTimeSeconds };
  });

  readonly sheetContext: Signal<NumpadSheetContext> = computed((): NumpadSheetContext => {
    const sd: SessionDrillViewModel = this.sessionDrill();
    const editing: RunViewModel | null = this.editingRun();
    const runNumber: number =
      editing === null ? sd.runs.length + 1 : sd.runs.findIndex((r: RunViewModel) => r.id === editing.id) + 1;
    return {
      drillName: sd.drillName,
      runNumber,
      targetRuns: sd.targetRuns,
      secondaryLine: SZ_SCORING_LABELS[sd.scoring]
    };
  });

  readonly focal: Signal<SessionBestFocal | null> = computed(() => null);

  readonly tiles: Signal<readonly SessionBestTile[]> = computed((): readonly SessionBestTile[] => {
    const sd: SessionDrillViewModel = this.sessionDrill();
    const counts: readonly number[] = sd.runs
      .map((r: RunViewModel) => r.reps)
      .filter((v: number | null): v is number => v !== null);
    const total: number | null = counts.length === 0 ? null : counts.reduce((a: number, b: number) => a + b, 0);
    return [
      {
        label: this.translate.instant('session.repsLabel'),
        value: total === null ? '-' : total.toString(),
        unit: null,
        variant: 'default'
      },
      { label: 'Runs', value: sd.runs.length, unit: null, variant: 'lime' }
    ];
  });

  readonly rows: Signal<readonly SessionRunRow[]> = computed((): readonly SessionRunRow[] =>
    buildRunRows(this.sessionDrill(), this.translate.instant('session.numpad.repsUnit'))
  );

  openSheetForAdd(): void {
    this.editingRun.set(null);
    this.sheetResetTrigger.update((v: number) => v + 1);
    this.sheetOpen.set(true);
  }

  openSheetForEdit(runId: string): void {
    const run: RunViewModel | null = this.sessionDrill().runs.find((r: RunViewModel) => r.id === runId) ?? null;
    if (run === null) return;
    this.editingRun.set(run);
    this.sheetResetTrigger.update((v: number) => v + 1);
    this.sheetOpen.set(true);
  }

  closeSheet(): void {
    this.sheetOpen.set(false);
    this.editingRun.set(null);
  }

  onSheetSubmit(payload: RunInput): void {
    const editing: RunViewModel | null = this.editingRun();
    const instanceId: string = this.sessionDrill().instanceId;
    if (editing !== null) {
      this.runHandler.update(editing.id, payload);
    } else {
      this.runHandler.add(instanceId, payload);
    }
    this.closeSheet();
  }

  onSheetDelete(): void {
    const editing: RunViewModel | null = this.editingRun();
    if (editing === null) return;
    this.runHandler.delete(editing.id);
    this.closeSheet();
  }

  deleteRow(runId: string): void {
    this.confirmDialog
      .confirm$({
        header: this.translate.instant('session.run.deleteConfirm'),
        okLabel: this.translate.instant('session.run.deleteAction'),
        destructive: true
      })
      .pipe(
        filter((ok: boolean): boolean => ok),
        take(1)
      )
      .subscribe((): void => this.runHandler.delete(runId));
  }
}
